import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SeverityBadge } from "@/components/common/SeverityBadge";
import { ShieldCheck, ExternalLink, CheckCircle2, Radar, Bug, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const cveLink = (id) => id.startsWith("CVE-") ? `https://nvd.nist.gov/vuln/detail/${id}` : `https://www.google.com/search?q=${encodeURIComponent(id)}`;

const steps = (v) => [
  { t: "Confirmer la faille", d: `Revois la sortie du scan pour ${v.host}:${v.port} et valide la détection (${v.source}).` },
  { t: "Identifier le composant", d: `Localise le service vulnérable : ${v.service || "service inconnu"} exposé sur le port ${v.port}.` },
  { t: "Appliquer le correctif", d: `Mets à jour vers la dernière version corrigée. Consulte l'avis fournisseur pour ${v.id}.` },
  { t: "Restreindre l'accès", d: "En attendant le patch, filtre l'accès réseau à ce port (pare-feu / segmentation)." },
  { t: "Re-scanner", d: "Relance un scan défensif pour confirmer que la faille est corrigée." },
];

export default function Exploits() {
  const [vulns, setVulns] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.getVulnerabilities().then(d => { setVulns(d); setSelected(d[0] || null); }).catch(() => {});
  }, []);

  const count = vulns.length;
  const remediationSteps = useMemo(() => selected ? steps(selected) : [], [selected]);

  return (
    <div className="px-4 md:px-8 py-8 max-w-[1600px] mx-auto space-y-6 animate-fade-in" data-testid="remediation-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest mb-2">// MODULE 05 · REMEDIATION</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><ShieldCheck className="h-8 w-8 text-primary"/> Console de remédiation</h1>
          <p className="text-muted-foreground mt-1 text-sm">Pour chaque faille détectée, suis le plan de correction pas à pas.</p>
        </div>
        <Badge variant="outline" className="font-mono text-[10px] border-primary/40 text-primary gap-2 py-1.5"><Bug className="h-3.5 w-3.5"/>{count} FAILLES</Badge>
      </div>

      {count === 0 ? (
        <Card className="bg-card/60 border-border p-12 text-center" data-testid="remediation-empty">
          <ShieldCheck className="h-8 w-8 text-muted-foreground mx-auto mb-3"/>
          <div className="text-muted-foreground font-mono text-sm mb-4">Aucune faille à corriger. Lance un scan avec corrélation CVE.</div>
          <Link to="/scanner"><Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs gap-2"><Radar className="h-3.5 w-3.5"/>LANCER UN SCAN</Button></Link>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <Card className="bg-card/60 border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-0.5">// FAILLES DÉTECTÉES</div>
                <div className="font-semibold text-sm">Sélectionne une faille</div>
              </div>
              <div className="max-h-[560px] overflow-y-auto divide-y divide-border">
                {vulns.map((v, i) => (
                  <button key={`${v.id}-${i}`} data-testid={`remediation-item-${v.id}`} onClick={() => setSelected(v)} className={cn(
                    "w-full text-left px-4 py-3 transition-colors",
                    selected && selected.id === v.id && selected.host === v.host && selected.port === v.port
                      ? "bg-primary/10 border-l-2 border-primary" : "hover:bg-muted/30 border-l-2 border-transparent"
                  )}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs text-primary font-bold">{v.id}</span>
                      <SeverityBadge level={v.severity}/>
                    </div>
                    <div className="text-sm font-medium leading-snug">{v.title}</div>
                    <div className="mt-1.5 flex items-center justify-between text-[11px] font-mono text-muted-foreground">
                      <span>{v.host}:{v.port}</span>
                      <span className="text-warning">CVSS {Number(v.cvss || 0).toFixed(1)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          <div className="lg:col-span-8 space-y-4">
            {selected && (
              <>
                <Card className="bg-card/60 border-border p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="font-mono text-xs text-primary">{selected.id}</div>
                      <div className="font-bold text-lg mt-0.5">{selected.title}</div>
                      <div className="font-mono text-xs text-muted-foreground mt-1">{selected.host}:{selected.port} · {selected.source}</div>
                    </div>
                    <SeverityBadge level={selected.severity}/>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 font-mono text-xs">
                    <div className="p-3 rounded border border-border bg-background"><div className="text-muted-foreground text-[10px] tracking-widest">HÔTE</div><div className="mt-1">{selected.host}</div></div>
                    <div className="p-3 rounded border border-border bg-background"><div className="text-muted-foreground text-[10px] tracking-widest">SERVICE</div><div className="mt-1 uppercase">{selected.service || "—"}</div></div>
                    <div className="p-3 rounded border border-border bg-background"><div className="text-muted-foreground text-[10px] tracking-widest">CVSS</div><div className="mt-1 text-warning font-bold">{Number(selected.cvss || 0).toFixed(1)}</div></div>
                    <div className="p-3 rounded border border-border bg-background"><div className="text-muted-foreground text-[10px] tracking-widest">ÉTAT</div><div className="mt-1 uppercase">{selected.state}</div></div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-5">
                    <a href={cveLink(selected.id)} target="_blank" rel="noreferrer">
                      <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs gap-2 shadow-red-glow"><ExternalLink className="h-3.5 w-3.5"/>AVIS & CORRECTIF</Button>
                    </a>
                    <Link to="/scanner"><Button variant="outline" className="font-mono text-xs gap-2 border-border"><Radar className="h-3.5 w-3.5"/>RE-SCANNER</Button></Link>
                  </div>
                </Card>

                <Card className="bg-card/60 border-border p-5">
                  <div className="font-mono text-[10px] tracking-widest text-success mb-3 flex items-center gap-1.5"><Lock className="h-3 w-3"/>// PLAN DE REMÉDIATION</div>
                  <div className="space-y-2">
                    {remediationSteps.map((s, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-md border border-border bg-background">
                        <div className="h-6 w-6 rounded-full flex items-center justify-center border border-success/50 bg-success/15 text-success shrink-0"><CheckCircle2 className="h-3.5 w-3.5"/></div>
                        <div>
                          <div className="text-sm font-medium">{s.t}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{s.d}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
